import React from "react";
import { Link } from "react-router-dom";
import {
  FiArrowUpRight,
  FiCheck,
  FiCode,
  FiDatabase,
  FiLayers,
  FiServer,
} from "react-icons/fi";

const technologyGroups = [
  {
    title: "Front-end experiences",
    description: "Fast, accessible interfaces that feel effortless to use.",
    icon: FiCode,
    technologies: [
      { name: "React", image: "/Images/react.png" },
      { name: "Next.js", image: "/Images/next.png" },
      { name: "Angular", image: "/Images/angular.webp" },
      { name: "JavaScript", image: "/Images/js.png" },
    ],
  },
  {
    title: "Back-end engineering",
    description: "Reliable systems designed for performance and scale.",
    icon: FiServer,
    technologies: [
      { name: "Node.js", image: "/Images/node.png" },
      { name: "PHP", image: "/Images/php.webp" },
      { name: "Python", image: "/Images/python.webp" },
      { name: ".NET", image: "/Images/dotnet.webp" },
    ],
  },
  {
    title: "Platforms and frameworks",
    description: "Flexible foundations matched to your content and workflows.",
    icon: FiLayers,
    technologies: [
      { name: "WordPress", image: "/Images/wordpress.png" },
      { name: "Laravel", image: "/Images/Laravel.png" },
      { name: "SharePoint", image: "/Images/sharepoint.png" },
      { name: "Bootstrap", image: "/Images/bootstrap.webp" },
    ],
  },
  {
    title: "Data and integrations",
    description: "Connected data that stays structured, secure, and useful.",
    icon: FiDatabase,
    technologies: [
      { name: "MySQL", image: "/Images/MySQL.png" },
      { name: "MongoDB", image: "/Images/MongoDB.png" },
      { name: "PostgreSQL", image: "/Images/postgre.png" },
      { name: "Redis", image: "/Images/Redis.png" },
    ],
  },
];

const deliveryBenefits = [
  "Technology selected around your goals",
  "Scalable architecture without unnecessary complexity",
  "Transparent delivery from planning through launch",
];

const WebsiteStack = () => {
  return (
    <section className="relative isolate overflow-hidden bg-slate-50 px-5 py-20 md:px-8 md:py-24 lg:px-12 lg:py-28">
      <div className="absolute inset-0 -z-20 bg-[radial-gradient(circle_at_8%_12%,rgba(100,60,144,0.13),transparent_28%),radial-gradient(circle_at_92%_84%,rgba(255,219,22,0.17),transparent_27%)]" />
      <div className="absolute inset-0 -z-10 opacity-[0.04] [background-image:linear-gradient(rgba(15,23,42,0.5)_1px,transparent_1px),linear-gradient(90deg,rgba(15,23,42,0.5)_1px,transparent_1px)] [background-size:64px_64px]" />

      <div className="mx-auto grid max-w-7xl items-start gap-14 lg:grid-cols-[0.78fr_1.22fr] lg:gap-16 xl:gap-24">
        <div className="lg:sticky lg:top-28">
          <div className="inline-flex items-center gap-3 rounded-full border border-slate-200 bg-white px-4 py-2 shadow-sm">
            <span className="h-2.5 w-2.5 rounded-full bg-[#643c90]" />
            <span className="text-sm font-bold tracking-wide text-slate-700">
              Custom development expertise
            </span>
          </div>

          <h2 className="mt-6 max-w-2xl font-heading text-4xl font-extrabold leading-[1.07] tracking-[-0.045em] text-slate-950 md:text-5xl lg:text-[3.45rem]">
            A custom web development company
            <span className="block text-[#643c90]">built around your goals.</span>
          </h2>

          <p className="mt-6 max-w-xl text-lg leading-8 text-slate-600">
            We combine product thinking, design, and engineering to build web
            platforms that solve the right problem today and remain ready for
            what your business needs next.
          </p>

          <div className="mt-7 space-y-4">
            {deliveryBenefits.map((benefit) => (
              <div key={benefit} className="flex items-start gap-3">
                <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#643c90]/10 text-[#643c90]">
                  <FiCheck className="h-4 w-4" />
                </span>
                <span className="font-semibold leading-7 text-slate-700">{benefit}</span>
              </div>
            ))}
          </div>

          <Link
            to="/contactus"
            className="group mt-9 inline-flex min-h-[52px] items-center justify-center gap-2 rounded-full bg-slate-950 px-7 py-3 font-bold text-white transition duration-300 hover:-translate-y-0.5 hover:bg-[#643c90] hover:shadow-[0_16px_40px_rgba(100,60,144,0.2)]"
          >
            Plan your website
            <FiArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </Link>
        </div>

        <div>
          <div className="mb-7 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-[#643c90]">
                Our technology toolkit
              </p>
              <h3 className="mt-2 font-heading text-2xl font-extrabold text-slate-950 md:text-3xl">
                The right tools for the right challenge
              </h3>
            </div>
            <p className="max-w-xs text-sm leading-6 text-slate-500 sm:text-right">
              Chosen for fit and maintainability—not because they are fashionable.
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            {technologyGroups.map(({ title, description, icon: Icon, technologies }) => (
              <article
                key={title}
                className="group rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm transition duration-300 hover:-translate-y-1 hover:border-[#643c90]/30 hover:shadow-xl hover:shadow-slate-200/60 md:p-6"
              >
                <div className="flex items-start gap-4">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#643c90] text-white shadow-[0_12px_28px_rgba(100,60,144,0.22)]">
                    <Icon className="h-5 w-5" />
                  </span>
                  <div>
                    <h4 className="font-heading text-lg font-extrabold text-slate-950">
                      {title}
                    </h4>
                    <p className="mt-1 text-sm leading-6 text-slate-500">{description}</p>
                  </div>
                </div>

                <div className="mt-5 grid grid-cols-2 gap-2.5">
                  {technologies.map(({ name, image }) => (
                    <div
                      key={name}
                      className="flex min-w-0 items-center gap-2.5 rounded-xl border border-slate-100 bg-slate-50 px-3 py-2.5 transition group-hover:bg-white"
                    >
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white shadow-sm">
                        <img
                          src={image}
                          alt=""
                          loading="lazy"
                          className="h-5 w-5 object-contain"
                        />
                      </span>
                      <span className="truncate text-xs font-bold text-slate-700 sm:text-sm">
                        {name}
                      </span>
                    </div>
                  ))}
                </div>
              </article>
            ))}
          </div>

          <div className="mt-4 flex items-start gap-4 rounded-[22px] bg-[#080b12] p-5 text-white md:p-6">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#FFDB16] text-slate-950">
              <FiLayers className="h-5 w-5" />
            </span>
            <div>
              <h4 className="font-heading text-lg font-extrabold">One accountable team</h4>
              <p className="mt-1 leading-7 text-slate-400">
                Strategy, interface, engineering, data, and launch work together as one delivery process.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WebsiteStack;
